import {useState,useEffect,useRef} from "react";
import {Link,useNavigate} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import {ListSubheader,useTheme,Menu,Container,Avatar,Tooltip,MenuItem,AppBar,Box,Toolbar,IconButton,Typography,Collapse} from '@mui/material';
import {alpha} from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import About from '../../custom/components/About.js';
import {getCookie} from '../utilities/Utilities.js';//CORE
import {getStringLang} from '../utilities/Strings.js';//CORE
import {useApplication} from './ApplicationContext.js';//CORE
import {useNotifications} from './Notification/NotificationsContext.js';//CORE
import {getIconComponent} from "../../custom/icons/iconLoader";
import NotificationsPopup from "./Notification/NotificationsPopup.js";
import {consoleLog} from '../../core/utilities/Utilities.js';
import {io} from 'socket.io-client';

const language=getCookie('preferredLanguage')||'en';
const file='Header.js';
const socket=io({
  autoConnect:false,
  path:'/api/socket.io/',
  transports:['websocket'],
});

const userMenuAuthorization=[
    {
        "label": "UserMenuProfile",
        "name": "user",
        "path": "/user/",
        "visible": true
    },
    {
        "label": "UserMenuAbout",
        "name": "about",
        "visible": true
    },
    {
        "label": "UserMenuLogout",
        "name": "logout",
        "visible": true
    }
];

//avatarSrc: optional client profile picture (data URL/URL) for the user menu avatar - the icon stays as fallback
function Header({menuItems,userData,appName,avatarSrc}){
  const [anchorElNav,setAnchorElNav]=useState(null);
  const [anchorElUser,setAnchorElUser]=useState(null);
  const [collapsedGroups,setCollapsedGroups]=useState({});
  const [userSettings,setUserSettings]=useState([]);
  const userSettingsOffline=[{name:'login',label:'Login'}];
  const {keycloak}=useKeycloak();
  const isLoggedIn=keycloak.authenticated;
  const navigate=useNavigate();
  const [openAbout,setOpenAbout]=useState(false);
  const {globalData}=useApplication();
  const application={globalData,getLocale:(key,options)=>globalData?.getLocale(key,options)};
  const theme=useTheme();
  const {refreshNotifications}=useNotifications();
  const notificationsInitializedRef=useRef(false);
  const APP_NAME=application?.globalData?.custom_parameters?.APP_NAME_HEADER||appName||'Coda Application';//appName: optional client fallback, shown while logged out (no globalData yet)
  
  useEffect(()=>{
    if(globalData!==null){
      if(Object.keys(userMenuAuthorization).length>0){
        let userSettingsTemp=userMenuAuthorization;
        userSettingsTemp.forEach(item=>{
          if(item.name==='login'){item['visible']=!isLoggedIn}
          else if(item.name==='logout'){item['visible']=isLoggedIn}
          else{item['visible']=true}
        });
        setUserSettings(userSettingsTemp);
      }

    }
    // dependency handleCloseUserMenu is stable and does not cause renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[globalData,userData,isLoggedIn]);


  useEffect(()=>{
    if(!isLoggedIn){
      notificationsInitializedRef.current=false;
      return;
    }
    if(globalData!==null&&!notificationsInitializedRef.current){
      notificationsInitializedRef.current=true;
      refreshNotifications();
    }
  },[isLoggedIn,globalData,refreshNotifications]);

  useEffect(()=>{
    if(!isLoggedIn||!userData?.user_id) return;
    socket.auth={
      token:keycloak.token
    };
    if(!socket.connected){
      socket.connect();
      consoleLog({file,context:'socket connected',arg_name:'',arg_value:null});
    }
    
    const onRefreshNotification=(data)=>{
      consoleLog({file,context:'socket refresh_notification',arg_name:'data',arg_value:data});
      refreshNotifications();
    };
    socket.on('refresh_notification',onRefreshNotification);
    return()=>{
      socket.off('refresh_notification',onRefreshNotification);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isLoggedIn,userData?.user_id]);

  useEffect(()=>{
    if(!isLoggedIn&&socket.connected){
      socket.disconnect();
      consoleLog({file,context:'socket disconnect',arg_name:'',arg_value:null});
    }
  },[isLoggedIn]);

  const handleToggleGroup=(appKey)=>{
    setCollapsedGroups(prev=>({...prev,[appKey]:!(prev[appKey]??true)}));
  };

  const handleItemSelect=(event,index)=>{
    handleCloseNavMenu();
  };

  const handleOpenNavMenu=(event)=>{
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu=(event)=>{
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu=()=>{
    setAnchorElNav(null);
  };

  const handleCloseUserMenu=(name,path)=>{
    switch(name){
      case "logout":
        navigate('/');
        sessionStorage.setItem("logCounter",0);
        keycloak.logout();
        break;
      case "login":
        localStorage.setItem('handleLoginClicked','Y');
        const returnUrl=localStorage.getItem('sessionExpiredReturnUrl');
        keycloak.login(returnUrl?{redirectUri:returnUrl}:undefined);
        break;
      case "about":
        setOpenAbout(true);
        break;
      default:
        if(path!==undefined&&path!=='backdropClick'){
          // If the current role exposes a "my_user" module (a simplified,
          // self-service profile screen - see MyUserDetail.js), prefer it:
          // it already resolves to the logged-in user with no id/context
          // needed. Otherwise fall back to the classic user detail route.
          const myUserModule=Object.values(menuItems?.appItems||{})
            .flatMap(app=>app.menuItems||[])
            .find(module=>module.name==='my_user');
          if(myUserModule){
            navigate(myUserModule.routes?.[myUserModule.defaultRoute]?.path||'/my_user');
          }
          else{
            const drilldown_id=userData.user_id;
            navigate(path+drilldown_id+'/audit');
          }
        }
        break;
    }
    setAnchorElUser(null);
  };

  const aboutCallback=()=>{
    setOpenAbout(false);
  };

  return (<>
    {openAbout&&<About aboutCallback={aboutCallback} application={application}></About>}
    <AppBar position="static" style={{backgroundColor:theme.palette.primary.main}}>
      <Container disableGutters maxWidth="xxl" style={{paddingLeft:'1px'}}>
        <Toolbar disableGutters style={{minHeight:'auto',height:'34px'}}>
          <Box sx={{flexGrow:1}}>
            {isLoggedIn&&
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              style={{color:"white"}}>
              {<MenuIcon/>}
            </IconButton>}
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{vertical:'bottom',horizontal:'left'}}
              keepMounted
              transformOrigin={{vertical:'top',horizontal:'left'}}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              //small gap under the bar + border/shadow so the menu's top edge never blends into the page behind it
              slotProps={{paper:{sx:{mt:0.5,mb:0,borderRadius:1.5,border:`1px solid ${theme.palette.divider}`,boxShadow:'0 8px 24px rgba(0,0,0,0.18)','& .MuiList-root':{pt:0,pb:0}}}}}>
              {globalData!==null&&menuItems.appDisplayOrder.map((appKey,appIndex)=>{
                const app=menuItems.appItems[appKey];
                if(!app) return null;
                const isCollapsible=appKey!=='Default';
                const isCollapsed=isCollapsible&&(collapsedGroups[appKey]??true);
                return (
                <Box key={appKey} component="div" sx={{listStyle:"none",p:0,m:0}}>
                  {app?.menuLabel&&
                  <ListSubheader
                    onClick={isCollapsible?()=>handleToggleGroup(appKey):undefined}
                    sx={{
                      cursor:isCollapsible?'pointer':'default',
                      fontSize:"12px",
                      fontWeight:"bold",
                      color:app?.menuColor,
                      backgroundColor:alpha(app?.menuColor||'#000000',0.10),
                      px:2,
                      pt:1,
                      pb:0.5,
                      lineHeight:1,
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'space-between',
                      borderTop:appIndex>0?`1px solid ${theme.palette.divider}`:'none',//separates groups
                      borderBottom:`1px solid ${theme.palette.divider}`}}>
                    {application.getLocale(app?.menuLabel)}
                    {isCollapsible&&(isCollapsed?<ExpandMoreIcon sx={{fontSize:16}}/>:<ExpandLessIcon sx={{fontSize:16}}/>)}
                  </ListSubheader>}
                  <Collapse in={!isCollapsed} timeout="auto">
                    {app?.menuItems?.filter(menu=>menu?.visible!==false).map((menu,index)=>{
                      const Icon=getIconComponent(menu?.iconName||"defaultEntityIcon");
                      return(
                      <MenuItem
                        key={menu.name}
                        component={Link}
                        to={'defaultPath' in menu.routes[menu.defaultRoute]?menu.routes[menu.defaultRoute].defaultPath:menu.routes[menu.defaultRoute].path}
                        onClick={(event)=>handleItemSelect(event,index)}
                        sx={{
                          backgroundColor:alpha(app?.menuColor||'#000000',0.05),
                          "&:hover":{backgroundColor:alpha(app?.menuColor||'#000000',0.25)},
                          px:2,
                          textDecoration:"none",
                          color:app?.menuColor}}>
                        {Icon&&<Icon sx={{color:app?.menuColor}}/>}
                        <span
                          style={{
                            fontSize:"16px",
                            width:"100%",
                            marginLeft:"5px",
                          }}>
                          {application.getLocale(menu?.label)}
                        </span>
                      </MenuItem>)
                    })}
                  </Collapse>
                </Box>)
              })}
            </Menu>
          </Box>
          <Box sx={{flexGrow:1,display:'flex',alignItems:'center'}}>
            <Typography
              variant="h6"
              sx={{
                color:'white',
                fontSize:{
                  xs:'0.85rem',//phones
                  sm:'1rem',//tablets
                  md:'1.25rem',//desktop (h6 default)
                },
                lineHeight: 1,
              }}>
            {APP_NAME}
            </Typography>
          </Box>
          <Box sx={{flexGrow:0}}>
            {isLoggedIn&&<NotificationsPopup application={application}/>}
            <Tooltip title={'role_name' in userData?globalData&&application.getLocale('TooltipOptions'):application.getLocale('TooltipOptions')}>
              <IconButton onClick={handleOpenUserMenu} size="small">
                <Avatar alt={'user_name' in userData?userData.user_name:''} src={(isLoggedIn&&avatarSrc)||undefined} sx={{width:24,height:24}}>
                  <AccountCircleRoundedIcon/>
                </Avatar>
              </IconButton>
            </Tooltip>            
            <Menu
              id="menu-user"
              anchorEl={anchorElUser}
              anchorOrigin={{vertical:'bottom',horizontal:'right'}}
              keepMounted
              transformOrigin={{vertical:'top',horizontal:'right'}}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}>
                {!isLoggedIn&&userSettingsOffline.map((setting)=>{
                 return (
                  <MenuItem key={setting.name} onClick={()=>handleCloseUserMenu(setting.name,'')}>
                    <Typography textAlign="center">{getStringLang('Login',language)}</Typography>
                  </MenuItem>);
                })}
              {isLoggedIn&&userSettings.map((setting)=>{
                return (setting.visible&&
                  <MenuItem key={setting.name} onClick={()=>handleCloseUserMenu(setting.name,setting.path)}>
                    <Typography textAlign="center">{setting.label&&globalData?application.getLocale(setting?.label):getStringLang('Login',language)}</Typography>
                  </MenuItem>);
              })}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
    </>
  );
}
export default Header;
